import { FetchClientError } from '@veupathdb/http-utils';
import {
  Action,
  DETAIL_LOADING,
  DETAIL_RECEIVED,
  DETAIL_ERROR,
  DETAIL_UPDATING,
  DETAIL_UPDATE_SUCCESS,
  DETAIL_UPDATE_ERROR,
  DETAIL_REMOVING,
  DETAIL_REMOVE_SUCCESS,
  DETAIL_REMOVE_ERROR,
  SHARING_SUCCESS,
  SHARING_MODAL_OPEN,
  SHARING_DATASET_PENDING,
  SHARING_ERROR,
  updateDatasetCommunityVisibilityError,
  updateDatasetCommunityVisibilityPending,
  updateDatasetCommunityVisibilitySuccess,
  METADATA_LOADING,
  METADATA_RECEIVED,
  SEARCHES_RECEIVED,
} from '../Actions/UserDatasetsActions';

import { DatasetGetResponseBody, VdiServiceMetadata } from '../Service';
import { CommunityPromotionError } from '../Components/Sharing/CommunityPromotionError';
import { Question } from '@veupathdb/wdk-client/lib/Utils/WdkModel';

export const key = 'userDatasetDetail';

/**
 * If isLoading is false, and resource is undefined,
 * then assume the user dataset does not exist
 */
export type UserDatasetEntry = {
  isLoading: boolean;
  resource?: DatasetGetResponseBody;
};

export interface State {
  userDatasetDetails?: UserDatasetEntry;
  userDatasetUpdating: boolean;
  userDatasetLoading: boolean;
  userDatasetRemoving: boolean;
  sharingModalOpen: boolean;
  sharingDatasetPending: boolean;
  loadError?: FetchClientError;
  updateError?: FetchClientError;
  removalError?: FetchClientError;
  shareError: Error | undefined;
  shareSuccessful: boolean | undefined;
  updateDatasetCommunityVisibilityPending: boolean;
  updateDatasetCommunityVisibilitySuccess: boolean;
  updateDatasetCommunityVisibilityError: undefined | CommunityPromotionError;
  serviceMetadata?: VdiServiceMetadata;
  /**
   * Searches fetched fresh on install, tagged with the dataset type they were
   * fetched for. Preferred over the cached globalData.questions — which
   * cannot contain a dataset installed during this session — but only when
   * userDatasetType matches the dataset currently being viewed; otherwise
   * these are leftover searches from a different dataset type and must be
   * ignored in favor of the questionMap fallback.
   */
  userDatasetSearches?: {
    userDatasetType: string;
    searches: Question[];
  };
}

const initialState: State = {
  userDatasetLoading: false,
  userDatasetUpdating: false,
  userDatasetRemoving: false,
  sharingModalOpen: false,
  sharingDatasetPending: false,
  shareError: undefined,
  shareSuccessful: undefined,
  updateDatasetCommunityVisibilityError: undefined,
  updateDatasetCommunityVisibilityPending: false,
  updateDatasetCommunityVisibilitySuccess: false,
  serviceMetadata: undefined,
};

/**
 * Stores a map of userDatasets by id. By not storing the current userDataset,
 * we avoid race conditions where the DATASET_DETAIL_RECEIVED actions are
 * dispatched in a different order than the corresponding action creators are
 * invoked.
 */
export function reduce(state: State = initialState, action: Action): State {
  switch (action.type) {
    case DETAIL_LOADING:
      return {
        ...state,
        userDatasetDetails: { isLoading: true },
      };

    case DETAIL_RECEIVED:
      return {
        ...state,
        userDatasetLoading: false,
        userDatasetDetails: {
          isLoading: false,
          resource: action.payload.userDataset,
        },
      };

    case DETAIL_ERROR:
      return {
        ...state,
        userDatasetLoading: false,
        loadError: action.payload.error,
      };

    case DETAIL_UPDATING:
      return {
        ...state,
        userDatasetUpdating: true,
        updateError: undefined,
      };

    case DETAIL_UPDATE_SUCCESS:
      return {
        ...state,
        userDatasetUpdating: false,
        userDatasetDetails: {
          isLoading: false,
          resource: action.payload.userDataset,
        },
      };

    case DETAIL_UPDATE_ERROR:
      return {
        ...state,
        userDatasetUpdating: false,
        updateError: action.payload.error,
      };

    case DETAIL_REMOVING:
      return {
        ...state,
        userDatasetRemoving: true,
      };

    case DETAIL_REMOVE_SUCCESS:
      return {
        ...state,
        userDatasetRemoving: false,
        removalError: undefined,
      };

    case DETAIL_REMOVE_ERROR:
      return {
        ...state,
        userDatasetRemoving: false,
        removalError: action.payload.error,
      };

    case SHARING_SUCCESS:
      return {
        ...state,
        sharingDatasetPending: false,
        shareSuccessful: action.payload.shareSuccessful,
      };

    case SHARING_MODAL_OPEN:
      return {
        ...state,
        sharingModalOpen: action.payload.sharingModalOpen,
      };

    case SHARING_DATASET_PENDING:
      return {
        ...state,
        sharingDatasetPending: action.payload.sharingDatasetPending,
      };

    case SHARING_ERROR:
      return {
        ...state,
        sharingDatasetPending: false,
        shareError: action.payload.shareError,
      };

    case METADATA_LOADING:
      return state;

    case METADATA_RECEIVED:
      return {
        ...state,
        serviceMetadata: action.payload,
      };

    case SEARCHES_RECEIVED:
      return {
        ...state,
        userDatasetSearches: {
          userDatasetType: action.payload.userDatasetType,
          searches: action.payload.searches,
        },
      };

    case updateDatasetCommunityVisibilityError.type:
      return {
        ...state,
        updateDatasetCommunityVisibilityPending: false,
        updateDatasetCommunityVisibilityError: action.payload.errorMessage,
      };

    case updateDatasetCommunityVisibilityPending.type:
      return {
        ...state,
        updateDatasetCommunityVisibilityPending: true,
      };

    case updateDatasetCommunityVisibilitySuccess.type:
      return {
        ...state,
        updateDatasetCommunityVisibilityPending: false,
        updateDatasetCommunityVisibilitySuccess: action.payload.success ?? true,
      };

    default:
      return state;
  }
}
